import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";

export interface LinkedInConnectionStatusParams {
  accountId: string;
  identifier: string;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useLinkedInConnectionStatus(
  params: Ref<LinkedInConnectionStatusParams | null> | LinkedInConnectionStatusParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["linkedin-connection-status", "disabled"];
    return ["linkedin-connection-status", p.accountId, p.identifier];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.accountId && !!p.identifier;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const response = await Unipile.checkConnectionStatus(p.accountId, p.identifier);

      if (response.data?.status) {
        return response.data.data;
      }

      throw new Error("Failed to check LinkedIn connection status");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
